import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Button } from '@/components/ui/button';
import { 
  Plus, 
  GripVertical, 
  ChevronRight, 
  ChevronDown,
  Trash,
  BookOpen
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UnifiedItem, RecommendationGroup, Quotation } from './types';
import { LibraryBrowser } from './LibraryBrowser';
import { cn } from '@/lib/utils';
import { RecommendationItems } from './RecommendationItems';
import { libraryService } from '@/lib/supabase/library';
import { supabase } from '@/lib/supabase/client';
import { RichTextEditor } from '@/components/ui/rich-text-editor';

interface RecommendationsTabProps {
  quotation: Quotation;
  handleChange: (field: keyof Quotation, value: any) => void;
}

export function RecommendationsTab({ quotation, handleChange }: RecommendationsTabProps) {
  const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>([]);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [newGroupTitle, setNewGroupTitle] = useState('');
  const [loading, setLoading] = useState(true);

  // Ensure recommendationGroups is initialized
  useEffect(() => {
    if (!quotation.recommendationGroups) {
      handleChange('recommendationGroups', []);
    }
    
    if (!quotation.recommendations) {
      handleChange('recommendations', []);
    }
    
    setLoading(false);
  }, [quotation, handleChange]);

  // Fix for missing recommendations
  useEffect(() => {
    // Skip if no quotation data
    if (!quotation) return;
    
    // If we have groups with itemIds but no recommendations
    if (quotation.recommendationGroups?.length > 0 && 
        quotation.recommendationGroups.some(g => g.itemIds?.length > 0) && 
        (!quotation.recommendations || quotation.recommendations.length === 0)) {
      
      // Collect all item IDs from all groups
      const allItemIds = quotation.recommendationGroups
        .flatMap(group => group.itemIds || [])
        .filter(Boolean);
      
      if (allItemIds.length === 0) return;
      
      console.log("Missing recommendations detected. Fetching from library:", allItemIds.length);
      
      // Fetch the missing recommendations from the library service
      const fetchMissingItems = async () => {
        try {
          // MODIFICADO: Em vez de usar o cache, fazer busca direta ao Supabase
          console.log('Buscando itens diretamente do Supabase (ignorando cache)');
          
          // Buscar itens diretamente do Supabase
          const { data, error } = await supabase
            .from('library_items')
            .select('*')
            .in('id', allItemIds);
            
          if (error) {
            console.error("Erro ao buscar do Supabase:", error);
            return;
          }
          
          if (data && data.length > 0) {
            console.log(`Supabase retornou ${data.length} itens`);
            
            // Convert library items to recommendation items
            const recommendations = data.map(item => ({
              id: item.id,
              type: item.type,
              globalOrder: 0,
              [item.type]: item.data
            }));
            
            // Update the quotation
            handleChange('recommendations', recommendations);
          } else {
            console.log("Nenhum item encontrado no Supabase");
            
            // Fallback para o método original
            // Get all library items
            const allLibraryItems = await libraryService.getItems();
            
            // Filter to only the ones we need
            const neededItems = allLibraryItems.filter(item => 
              allItemIds.includes(item.id)
            );
            
            if (neededItems.length > 0) {
              console.log(`Retrieved ${neededItems.length} items from library service`);
              
              // Convert library items to recommendation items
              const recommendations = neededItems.map(item => ({
                id: item.id,
                type: item.type,
                globalOrder: 0,
                [item.type]: item.data
              }));
              
              // Update the quotation
              handleChange('recommendations', recommendations);
            } else {
              console.log("No matching items found in library");
            }
          }
        } catch (error) {
          console.error("Error in fetchMissingItems:", error);
        }
      };
      
      fetchMissingItems();
    }
  }, [quotation, handleChange]);

  // Gerenciamento de grupos
  const handleAddGroup = () => {
    const newGroup: RecommendationGroup = {
      id: `group-${Date.now()}`,
      title: 'Novo Grupo',
      order: (quotation.recommendationGroups || []).length,
      itemIds: []
    };

    handleChange('recommendationGroups', [
      ...(quotation.recommendationGroups || []),
      newGroup
    ]);
    setEditingGroupId(newGroup.id);
    // Expandir o novo grupo automaticamente
    setExpandedGroupIds(prev => [...prev, newGroup.id]);
  };

  const handleUpdateGroup = (groupId: string, field: keyof RecommendationGroup, value: any) => {
    // Log para depuração ao atualizar a descrição
    if (field === 'description') {
      console.log('DEBUG - Atualizando descrição do grupo:', {
        groupId,
        value,
        valueType: typeof value,
        valueLength: value?.length || 0,
        valueSample: value?.substring(0, 50) || 'N/A',
        // Log para verificar se o conteúdo HTML está correto
        htmlContent: value
      });
    }
    
    const updatedGroups = (quotation.recommendationGroups || []).map(group => {
      if (group.id === groupId) {
        const updatedGroup = { ...group, [field]: value };
        
        // Log para verificar o resultado do grupo atualizado
        if (field === 'description') {
          console.log('DEBUG - Grupo após atualização:', {
            id: updatedGroup.id,
            title: updatedGroup.title,
            hasDescription: !!updatedGroup.description,
            descriptionType: typeof updatedGroup.description,
            descriptionLength: updatedGroup.description?.length || 0
          });
        }
        
        return updatedGroup;
      }
      return group;
    });
    handleChange('recommendationGroups', updatedGroups);
  };

  const handleDeleteGroup = (groupId: string) => {
    const updatedGroups = (quotation.recommendationGroups || []).filter(
      group => group.id !== groupId
    );
    handleChange('recommendationGroups', updatedGroups);
    // Remover o grupo da lista de expandidos
    setExpandedGroupIds(prev => prev.filter(id => id !== groupId));
  };

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroupIds(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  // Drag and Drop handlers
  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId, type } = result;

    if (!destination) return;

    if (destination.droppableId === source.droppableId && 
        destination.index === source.index) {
      return;
    }

    if (type === 'group') {
      // Reordenar grupos
      const groups = Array.from(quotation.recommendationGroups || []);
      const [removed] = groups.splice(source.index, 1);
      groups.splice(destination.index, 0, removed);

      // Atualizar ordem
      const updatedGroups = groups.map((group, index) => ({
        ...group,
        order: index
      }));

      handleChange('recommendationGroups', updatedGroups);
    } else if (type === 'item') {
      const sourceGroup = quotation.recommendationGroups?.find(g => g.id === source.droppableId);
      const destGroup = quotation.recommendationGroups?.find(g => g.id === destination.droppableId);

      if (!sourceGroup || !destGroup) return;

      if (source.droppableId === destination.droppableId) {
        // Reordenar dentro do mesmo grupo
        const newItemIds = Array.from(sourceGroup.itemIds);
        newItemIds.splice(source.index, 1);
        newItemIds.splice(destination.index, 0, draggableId);

        const updatedGroups = quotation.recommendationGroups?.map(group =>
          group.id === sourceGroup.id 
            ? { ...group, itemIds: newItemIds }
            : group
        );

        handleChange('recommendationGroups', updatedGroups);
      } else {
        // Mover entre grupos diferentes
        const sourceItemIds = Array.from(sourceGroup.itemIds);
        const destItemIds = Array.from(destGroup.itemIds);

        // Remover do grupo de origem
        sourceItemIds.splice(source.index, 1);
        // Adicionar ao grupo de destino
        destItemIds.splice(destination.index, 0, draggableId);

        const updatedGroups = quotation.recommendationGroups?.map(group => {
          if (group.id === sourceGroup.id) {
            return { ...group, itemIds: sourceItemIds };
          }
          if (group.id === destGroup.id) {
            return { ...group, itemIds: destItemIds };
          }
          return group;
        });

        handleChange('recommendationGroups', updatedGroups);
      }
    }
  };

  // Renderização dos grupos
  const renderGroups = () => {
    // Ensure we have valid arrays before rendering
    const groups = quotation.recommendationGroups || [];
    
    if (loading) {
      return <div className="py-4 text-gray-500 text-center">Carregando grupos...</div>;
    }
    
    if (groups.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg border border-dashed">
          <BookOpen className="h-10 w-10 text-gray-300 mb-2" />
          <p className="text-gray-500 mb-4">Nenhum grupo de recomendações adicionado</p>
          <Button onClick={handleAddGroup}>
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Grupo
          </Button>
        </div>
      );
    }
    
    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="groups" type="group">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="space-y-3"
            >
              {groups.map((group, index) => (
                <Draggable
                  key={group.id}
                  draggableId={group.id}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={cn(
                        "rounded-lg border bg-card text-card-foreground shadow-sm",
                        snapshot.isDragging && "shadow-lg"
                      )}
                    >
                      <div className="flex items-center p-4">
                        <div
                          {...provided.dragHandleProps}
                          className="flex items-center mr-2"
                        >
                          <GripVertical className="h-5 w-5 text-gray-400" />
                        </div>

                        {editingGroupId === group.id ? (
                          <Input
                            value={newGroupTitle || group.title}
                            onChange={(e) => setNewGroupTitle(e.target.value)}
                            onBlur={() => {
                              handleUpdateGroup(group.id, 'title', newGroupTitle);
                              setEditingGroupId(null);
                              setNewGroupTitle('');
                            }}
                            className="flex-1 mr-2"
                            autoFocus
                          />
                        ) : (
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() => toggleGroupExpansion(group.id)}
                          >
                            <div className="font-medium flex items-center">
                              {expandedGroupIds.includes(group.id) ? (
                                <ChevronDown className="h-4 w-4 mr-1" />
                              ) : (
                                <ChevronRight className="h-4 w-4 mr-1" />
                              )}
                              {group.title}
                            </div>
                            <div className="text-sm text-gray-500">
                              {group.itemIds.length} items
                            </div>
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingGroupId(group.id);
                              setNewGroupTitle(group.title);
                            }}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteGroup(group.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {expandedGroupIds.includes(group.id) && (
                        <div className="p-4 border-t">
                          <div className="mb-4">
                            <Label className="mb-2 block font-medium text-gray-700">Descrição do Grupo</Label>
                            <RichTextEditor
                              value={group.description || ''}
                              onChange={(value) => handleUpdateGroup(group.id, 'description', value)}
                              placeholder="Adicione uma descrição para este grupo de recomendações..."
                              rows={4}
                              className="mb-4"
                            />
                          </div>
                          <RecommendationItems
                            group={group}
                            quotation={quotation}
                            handleChange={handleChange}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Grupos de Recomendações</h2>
        <Button onClick={handleAddGroup}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Grupo
        </Button>
      </div>

      {renderGroups()}

      <LibraryBrowser
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        onSelectItem={(item) => {
          // Adicionar item ao grupo selecionado
          if (expandedGroupIds.length > 0) {
            const lastExpandedGroupId = expandedGroupIds[expandedGroupIds.length - 1];
            const group = quotation.recommendationGroups?.find(g => g.id === lastExpandedGroupId);
            if (group) {
              // Adicionar o ID do item ao grupo
              handleUpdateGroup(group.id, 'itemIds', [...group.itemIds, item.id]);
              
              // Adicionar o item ao array de recommendations se ainda não existir
              const existingItem = quotation.recommendations?.find(rec => rec.id === item.id);
              if (!existingItem) {
                handleChange('recommendations', [
                  ...(quotation.recommendations || []),
                  {
                    id: item.id,
                    type: item.type,
                    [item.type]: item
                  }
                ]);
              }
            }
          }
          setIsLibraryOpen(false);
        }}
      />
    </div>
  );
} 